import { useTranslation } from '../../../i18n';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Checkbox, Typography, Alert, message } from 'antd';
import { CloudServerOutlined, HistoryOutlined, ReloadOutlined, PlayCircleOutlined } from '@ant-design/icons';
import hostSharedService from '../../../../shared/services/hostSharedService';
import hostSharedAdminService from '../../../../admin/services/hostSharedAdminService';

const fmtSize = (b) => {
    if (!b) return '-';
    const u = ['B', 'KB', 'MB', 'GB', 'TB'];
    let i = 0;
    let v = b;
    while (v >= 1024 && i < u.length - 1) {
        v /= 1024;
        i++;
    }
    return `${v.toFixed(i ? 1 : 0)} ${u[i]}`;
};

/**
 * 异地备份(R2,每天一次,保留最近 N 份)。用户:列表 + 恢复;管理端:列表 + 立即备份。
 */
const OffsiteBackups = ({ vhostId, adminView = false }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [target, setTarget] = useState(null);
    const [opts, setOpts] = useState({ files: true, db: true });
    const [submitting, setSubmitting] = useState(false);
    const timer = useRef(null);

    const load = useCallback(async () => {
        if (!vhostId) return;
        setLoading(true);
        const res = adminView
            ? await hostSharedAdminService.getVhostOffsiteBackups(vhostId)
            : await hostSharedService.getOffsiteBackups(vhostId);
        setLoading(false);
        if (res?.success) {
            setData(res.data);
        } else {
            message.error(res?.message || res?.error || t('Failed to load backup list'));
        }
    }, [vhostId, adminView, t]);

    useEffect(() => {
        load();
    }, [load]);

    // 有恢复/备份在进行时轮询
    const restore = data?.restore;
    const busy = (restore && restore.status === 'running') || (data?.backups || []).some((b) => b.status !== 'completed');
    useEffect(() => {
        clearTimeout(timer.current);
        if (busy) {
            timer.current = setTimeout(async () => {
                if (!adminView && restore?.status === 'running') {
                    await hostSharedService.getOffsiteRestore(vhostId, restore.id);
                }
                load();
            }, 8000);
        }
        return () => clearTimeout(timer.current);
    }, [busy, restore, vhostId, adminView, load]);

    const doRestore = async () => {
        setSubmitting(true);
        const res = await hostSharedService.restoreOffsiteBackup(vhostId, target.id, { files: opts.files, db: opts.db && target.has_db });
        setSubmitting(false);
        if (res?.success) {
            message.success(t('Restore started'));
            setTarget(null);
            load();
        } else {
            message.error(res?.message || res?.error || t('Restore failed'));
        }
    };

    const runNow = async () => {
        const res = await hostSharedAdminService.runVhostOffsiteBackup(vhostId);
        if (res?.success) {
            message.success(t('Backup started'));
            load();
        } else {
            message.error(res?.message || res?.error || t('Failed to create backup'));
        }
    };

    const statusTag = (s) => {
        if (s === 'completed') return <Tag color="green">{t('Completed')}</Tag>;
        return <Tag color="processing">{t('In progress')}</Tag>;
    };

    const columns = [
        { title: t('Date'), dataIndex: 'date', key: 'date' },
        {
            title: t('Contents'),
            key: 'contents',
            render: (_, r) => (
                <Space size={4}>
                    <Tag>{t('Files')}</Tag>
                    {r.has_db && <Tag>{t('Database')}</Tag>}
                </Space>
            )
        },
        { title: t('Size'), dataIndex: 'size', key: 'size', render: fmtSize },
        { title: t('Status'), dataIndex: 'status', key: 'status', render: statusTag },
        ...(adminView ? [] : [{
            title: t('Actions'),
            key: 'action',
            render: (_, r) => (
                <Button
                    size="small"
                    icon={<HistoryOutlined />}
                    disabled={r.status !== 'completed' || busy}
                    onClick={() => {
                        setOpts({ files: true, db: !!r.has_db });
                        setTarget(r);
                    }}
                >
                    {t('Restore')}
                </Button>
            )
        }])
    ];

    return (
        <Card
            title={
                <Space>
                    <CloudServerOutlined />
                    {t('Off-site backups')}
                    {data && (
                        <Typography.Text type="secondary" style={{ fontWeight: 'normal' }}>
                            {t('Daily, the last {{n}} are kept', { n: data.keep })}
                        </Typography.Text>
                    )}
                </Space>
            }
            extra={
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>{t('Refresh')}</Button>
                    {adminView && <Button icon={<PlayCircleOutlined />} onClick={runNow} disabled={busy}>{t('Back up now')}</Button>}
                </Space>
            }
            style={{ marginTop: 16 }}
        >
            {data && !data.enabled && <Alert type="warning" showIcon message={t('Off-site backups are paused')} style={{ marginBottom: 12 }} />}
            {restore && restore.status !== 'done' && (
                <Alert
                    style={{ marginBottom: 12 }}
                    showIcon
                    type={restore.status === 'failed' ? 'error' : 'info'}
                    message={restore.status === 'failed' ? t('Restore failed') : t('Restoring…')}
                    description={restore.status === 'failed' ? restore.error : null}
                />
            )}
            <Table rowKey="id" size="small" dataSource={data?.backups || []} columns={columns} loading={loading} pagination={false}
                locale={{ emptyText: t('No off-site backups yet') }} />
            {adminView && (data?.failed || []).length > 0 && (
                <Typography.Text type="danger" style={{ display: 'block', marginTop: 8 }}>
                    {data.failed[0].local_date}: {data.failed[0].last_error}
                </Typography.Text>
            )}
            <Modal
                open={!!target}
                title={t('Restore from {{date}}', { date: target?.date })}
                okText={t('Restore')}
                okButtonProps={{ danger: true, disabled: !opts.files && !opts.db, loading: submitting }}
                onOk={doRestore}
                onCancel={() => setTarget(null)}
            >
                <Space direction="vertical">
                    <Checkbox checked={opts.files} onChange={(e) => setOpts({ ...opts, files: e.target.checked })}>{t('Files')}</Checkbox>
                    <Checkbox checked={opts.db} disabled={!target?.has_db} onChange={(e) => setOpts({ ...opts, db: e.target.checked })}>{t('Database')}</Checkbox>
                    <Typography.Text type="danger">{t('Current files and data will be overwritten.')}</Typography.Text>
                </Space>
            </Modal>
        </Card>
    );
};

export default OffsiteBackups;
